import type { TFunction } from 'i18next'
import { formatCNPJ, formatCPF, validateCNPJ, validateCPF } from '@/lib/utils/validation'
import { sdk } from '@/lib/sdk'
import type { EmployerIdType, TimeEstablishmentInput, TimeTrackingSetupConfig } from '@/types/timeTracking'

export const TT_LIMITS = { legalName: 150, workplace: 100, employeeName: 52, jobTitle: 100, esocial: 30, code: 14 } as const

export const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status

export const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/** The field the API named in `errors[0].param`, if any. */
export const paramOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { errors?: { param?: string }[] } } })?.response?.data?.errors?.[0]?.param

export const onlyDigits = (value: string | null | undefined): string => (value || '').replace(/\D/g, '')

export const formatEmployerId = (type: EmployerIdType | undefined, value: string | undefined): string =>
  !value ? '' : type === 'CPF' ? formatCPF(value) : formatCNPJ(value)

export const isEmployerIdValid = (type: EmployerIdType, value: string): boolean =>
  type === 'CPF' ? validateCPF(value) : validateCNPJ(value)

/** `YYYY-MM-DD` in a time zone (the community's). */
export const dayIn = (timeZone: string | undefined, date = new Date()): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

export const addDays = (day: string, days: number): string => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/** A `YYYY-MM-DD` day as the reader says it; the day is already the community's, so it is read in UTC. */
export const formatDay = (day: string | null | undefined, locale: string): string => {
  if (!day) return ''
  const [y, m, d] = day.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return day
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** `YYYY-MM-DDThh:mm:ss` (community time zone) split into the day and the time with seconds. */
export const splitLocal = (local: string | null | undefined, locale: string): { day: string; time: string } => {
  if (!local) return { day: '', time: '' }
  const [date, time = ''] = local.split('T')
  return { day: formatDay(date, locale), time: time.slice(0, 8) }
}

export const emptySetupConfig = (): TimeTrackingSetupConfig => ({
  mode: 'new',
  establishmentId: '',
  linkedEstablishmentId: null,
  employerIdType: 'CNPJ',
  employerId: '',
  caepf: '',
  cno: '',
  cei: '',
  legalName: '',
  workplace: '',
  responsibleCpf: ''
})

export type SetupErrors = Partial<Record<'establishmentId' | 'employerId' | 'caepf' | 'cno' | 'cei' | 'legalName' | 'workplace' | 'responsibleCpf' | 'form', string>>

/** `null` when the step can be saved; otherwise the per-field messages. */
export const validateSetup = (config: TimeTrackingSetupConfig, t: TFunction, { requireResponsible = true } = {}): SetupErrors | null => {
  const errors: SetupErrors = {}
  if (config.mode === 'existing') {
    if (!config.establishmentId) errors.establishmentId = t('timeTracking:establishment.errors.pick')
    return Object.keys(errors).length ? errors : null
  }
  const id = onlyDigits(config.employerId)
  if (!id) errors.employerId = t('timeTracking:establishment.errors.employerIdRequired')
  else if (!isEmployerIdValid(config.employerIdType, id)) errors.employerId = t(`timeTracking:establishment.errors.${config.employerIdType === 'CPF' ? 'cpfInvalid' : 'cnpjInvalid'}`)
  for (const key of ['caepf', 'cno', 'cei'] as const) {
    if (config[key] && onlyDigits(config[key]).length > TT_LIMITS.code) errors[key] = t('timeTracking:establishment.errors.codeInvalid')
  }
  if (!config.legalName.trim()) errors.legalName = t('timeTracking:establishment.errors.legalNameRequired')
  if (!config.workplace.trim()) errors.workplace = t('timeTracking:establishment.errors.workplaceRequired')
  const responsible = onlyDigits(config.responsibleCpf)
  if (responsible && !validateCPF(responsible)) errors.responsibleCpf = t('timeTracking:establishment.errors.cpfInvalid')
  else if (!responsible && requireResponsible) errors.responsibleCpf = t('timeTracking:establishment.errors.responsibleRequired')
  return Object.keys(errors).length ? errors : null
}

export const toEstablishmentInput = (config: TimeTrackingSetupConfig): TimeEstablishmentInput => ({
  employerIdType: config.employerIdType,
  employerId: onlyDigits(config.employerId),
  ...(onlyDigits(config.caepf) ? { caepf: onlyDigits(config.caepf) } : {}),
  ...(onlyDigits(config.cno) ? { cno: onlyDigits(config.cno) } : {}),
  ...(onlyDigits(config.cei) ? { cei: onlyDigits(config.cei) } : {}),
  legalName: config.legalName.trim(),
  workplace: config.workplace.trim(),
  ...(onlyDigits(config.responsibleCpf) ? { responsibleCpf: onlyDigits(config.responsibleCpf) } : {})
})

/**
 * The step's save, after the space exists: register the employer (or take the
 * one chosen) and point the space to it. A retry after the employer was saved
 * but the link was not finds it again by its document instead of failing on
 * "already registered".
 */
export const saveSetup = async (spaceId: string, config: TimeTrackingSetupConfig): Promise<void> => {
  if (config.mode === 'existing') {
    if (config.establishmentId && config.establishmentId === config.linkedEstablishmentId) return
    await sdk.timeTracking.linkSpace(spaceId, config.establishmentId)
    return
  }
  const input = toEstablishmentInput(config)
  let establishmentId: string
  try {
    establishmentId = (await sdk.timeTracking.createEstablishment(input)).id
  } catch (err) {
    if (statusOf(err) !== 409) throw err
    const existing = (await sdk.timeTracking.listEstablishments()).find((e) => e.employerId === input.employerId)
    if (!existing) throw err
    establishmentId = existing.id
  }
  await sdk.timeTracking.linkSpace(spaceId, establishmentId)
}

/** Per-field errors out of a refused save (the API names the field in `errors[0].param`). */
export const setupErrorsFromApi = (err: unknown): SetupErrors | null => {
  const message = messageOf(err)
  if (!message) return null
  const param = paramOf(err)
  const known = ['employerId', 'caepf', 'cno', 'cei', 'legalName', 'workplace', 'responsibleCpf', 'establishmentId'] as const
  const field = known.find((k) => k === param)
  return field ? { [field]: message } : { form: message }
}

/**
 * The AFD's collector: `01` is an installed mobile app, `02` a browser. The
 * app is a web app, so only when it runs installed on a phone (home screen,
 * no browser chrome) does it count as the mobile app.
 */
export const collectorOf = (): '01' | '02' => {
  try {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true
    const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
    return standalone && mobile ? '01' : '02'
  } catch {
    return '02'
  }
}

export const newRequestId = (): string => {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }
}

/** The browser's position, or `null` (denied, unavailable, too slow). Never blocks the punch. */
export const currentPosition = (timeoutMs = 8000): Promise<{ latitude: number; longitude: number; accuracyMeters: number } | null> =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) { resolve(null); return }
    let done = false
    const finish = (value: { latitude: number; longitude: number; accuracyMeters: number } | null) => { if (!done) { done = true; resolve(value) } }
    const timer = window.setTimeout(() => finish(null), timeoutMs + 1000)
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => { window.clearTimeout(timer); finish({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMeters: pos.coords.accuracy }) },
        () => { window.clearTimeout(timer); finish(null) },
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
      )
    } catch {
      window.clearTimeout(timer)
      finish(null)
    }
  })

/**
 * Downloads a private, short-lived file (the receipt PDF). The storage answers
 * it as an attachment, so following the link saves the file and the page
 * stays where it is.
 */
export const downloadLink = (url: string) => {
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** `YYYY-MM` of today in a time zone (the community's). */
export const monthIn = (timeZone: string | undefined, date = new Date()): string => dayIn(timeZone, date).slice(0, 7)

export const addMonthsTo = (month: string, delta: number): string => {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return d.toISOString().slice(0, 7)
}

/** "outubro de 2026" for `2026-10`. */
export const formatMonth = (month: string | null | undefined, locale: string): string => {
  if (!month) return ''
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1)))
}

/** Minutes as `h:mm` (`-1:05` for a debt). */
export const formatMinutes = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '—'
  const sign = minutes < 0 ? '-' : ''
  const abs = Math.abs(Math.round(minutes))
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`
}

/** `hh:mm` to minutes, or `null`. */
export const toMinutes = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = Number(m[1]); const min = Number(m[2])
  return h > 23 || min > 59 ? null : h * 60 + min
}

/** A weekday name (0 = Sunday) in the reader's language; 2026-10-04 is a Sunday. */
export const weekdayName = (weekday: number, locale: string, style: 'long' | 'short' = 'long'): string =>
  new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 4 + weekday)))

/** "21 de out." for `2026-10-21` (no year: the month is on screen). */
export const formatDayShort = (day: string, locale: string): string => {
  const [y, m, d] = day.split('-').map(Number)
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}
